import { fetchMaintenanceTypes } from '../components/Dashboard/tabs/maintenance.queries';

// Cache for maintenance type display names
let displayNamesCache: Record<string, string> | null = null;
let cachePromise: Promise<Record<string, string>> | null = null;

/**
 * Get the display name for a maintenance type.
 * Uses cached values to avoid repeated API calls.
 */
export const getMaintenanceTypeDisplayName = async (maintenanceType: string): Promise<string> => {
  // Return from cache if available
  if (displayNamesCache && displayNamesCache[maintenanceType]) {
    return displayNamesCache[maintenanceType];
  }

  // If cache is being loaded, wait for it
  if (cachePromise) {
    await cachePromise;
    return displayNamesCache?.[maintenanceType] || prettifyMaintenanceType(maintenanceType);
  }

  // Load the cache
  cachePromise = loadMaintenanceTypeDisplayNames();

  try {
    await cachePromise;
    return displayNamesCache?.[maintenanceType] || prettifyMaintenanceType(maintenanceType);
  } finally {
    cachePromise = null;
  }
};

/**
 * Load maintenance type display names from the API and cache them
 */
const loadMaintenanceTypeDisplayNames = async (): Promise<Record<string, string>> => {
  try {
    const response = await fetchMaintenanceTypes();
    displayNamesCache = response.display_names;
    return displayNamesCache;
  } catch (error) {
    console.error('Failed to load maintenance type display names:', error);
    // Return empty object on error
    return {};
  }
};

/**
 * Fallback function to prettify maintenance type if display name is not available
 */
export const prettifyMaintenanceType = (maintenanceType: string): string => {
  if (!maintenanceType) return 'Unknown';

  return maintenanceType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
};

/**
 * Synchronous version that returns the cached value or prettified name immediately
 * Use this when you need immediate access and can't await
 */
export const getMaintenanceTypeDisplayNameSync = (maintenanceType: string): string => {
  return displayNamesCache?.[maintenanceType] || prettifyMaintenanceType(maintenanceType);
};

/**
 * Preload the maintenance type display names cache
 * Call this early in the application lifecycle
 */
export const preloadMaintenanceTypeDisplayNames = async (): Promise<void> => {
  if (!displayNamesCache && !cachePromise) {
    cachePromise = loadMaintenanceTypeDisplayNames();
    await cachePromise;
    cachePromise = null;
  }
};
